import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import Select from "react-select";
import swal from "sweetalert";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { productSuggestionsOperations } from "state/ducks/store/productSuggestions";
import { uploadSuggestionMedia } from "./uploadMedia";
import { mediaUrl } from "./mediaUrl";
import { QUALITY_OPTIONS } from "./qualityOptions";

const PRIMARY = "#429853";

const SuggestionModal = (props) => {
  const { show, onHide, productId, ingredients, pictos, suggestion } = props;
  const dispatch = useDispatch();
  const isEdit = !!suggestion;

  const [name, setName] = useState("");
  const [badge, setBadge] = useState("");
  const [thematicBadgeId, setThematicBadgeId] = useState("");
  const [sort, setSort] = useState("");
  const [selected, setSelected] = useState([]);
  const [disponibilities, setDisponibilities] = useState([]);
  const [isEngaged, setIsEngaged] = useState(false);
  const [forcedBigBox, setForcedBigBox] = useState(false);
  const [massProduction, setMassProduction] = useState(false);
  const [activeOnSpot, setActiveOnSpot] = useState(true);
  const [activeOnShop, setActiveOnShop] = useState(true);
  const [mediaId, setMediaId] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Pré-remplissage en édition
  useEffect(() => {
    if (suggestion) {
      setName(suggestion.name || "");
      setBadge(suggestion.badge || "");
      setThematicBadgeId(suggestion.thematicBadge ? String(suggestion.thematicBadge.id) : "");
      setSort(suggestion.sort !== null && suggestion.sort !== undefined ? String(suggestion.sort) : "");
      setSelected(
        (suggestion.ingredients || []).map((i) => ({
          compositionProductId: i.compositionProductId,
          name: i.name,
          quantity: i.quantity || 1,
          quality: i.quality || (i.isSauce ? "normal" : null),
          isSauce: i.isSauce,
        }))
      );
      setDisponibilities(
        (suggestion.disponibilities || []).map((d) => ({
          startDate: d.startDate,
          endDate: d.endDate,
        }))
      );
      setIsEngaged(!!suggestion.isEngaged);
      setForcedBigBox(!!suggestion.forcedBigBox);
      setMassProduction(!!suggestion.massProduction);
      setActiveOnSpot(!!suggestion.activeOnSpot);
      setActiveOnShop(!!suggestion.activeOnShop);
      setMediaId(suggestion.media ? suggestion.media.id : null);
      setMediaPreview(mediaUrl(suggestion.media));
    }
  }, [suggestion]);

  const ingredientOptions = (ingredients || [])
    .filter((i) => !selected.find((s) => s.compositionProductId === i.id))
    .map((i) => ({ value: i.id, label: i.name, isSauce: i.isSauce }));

  const addIngredient = (option) => {
    if (!option) return;
    setSelected([
      ...selected,
      {
        compositionProductId: option.value,
        name: option.label,
        quantity: 1,
        quality: option.isSauce ? "normal" : null,
        isSauce: option.isSauce,
      },
    ]);
  };

  const changeQty = (id, delta) => {
    setSelected(
      selected.map((s) =>
        s.compositionProductId === id
          ? { ...s, quantity: Math.max(1, s.quantity + delta) }
          : s
      )
    );
  };

  const changeQuality = (id, quality) => {
    setSelected(
      selected.map((s) =>
        s.compositionProductId === id ? { ...s, quality } : s
      )
    );
  };

  const removeIngredient = (id) => {
    setSelected(selected.filter((s) => s.compositionProductId !== id));
  };

  const addDispo = () => setDisponibilities([...disponibilities, { startDate: "", endDate: "" }]);
  const changeDispo = (index, field, value) => {
    setDisponibilities(
      disponibilities.map((d, i) => (i === index ? { ...d, [field]: value } : d))
    );
  };
  const removeDispo = (index) =>
    setDisponibilities(disponibilities.filter((_, i) => i !== index));

  const onFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    uploadSuggestionMedia(file)
      .then((media) => {
        setMediaId(media.id);
        setMediaPreview(mediaUrl(media));
        setUploading(false);
      })
      .catch((err) => {
        setUploading(false);
        // Message de l'API (format, taille, droits…) : un texte fixe faisait
        // passer un refus d'authentification pour un problème de format.
        const data = err?.response?.data;
        const apiMessage = data?.result?.message || data?.message;
        swal(
          "Erreur",
          apiMessage
            ? `L'upload de l'image a échoué : ${apiMessage}`
            : "L'upload de l'image a échoué (formats JPEG/PNG, 4 Mo max).",
          "error"
        );
      });
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      swal("Titre manquant", "Merci de saisir un titre pour la suggestion.", "warning");
      return;
    }
    if (selected.length === 0) {
      swal("Ingrédients manquants", "Ajoutez au moins un ingrédient.", "warning");
      return;
    }

    const payload = {
      name: name.trim(),
      badge: badge.trim() || null,
      thematicBadgeId: thematicBadgeId ? parseInt(thematicBadgeId, 10) : null,
      sort: sort !== "" ? parseInt(sort, 10) : null,
      mediaId: mediaId,
      isEngaged,
      forcedBigBox,
      massProduction,
      activeOnSpot,
      activeOnShop,
      ingredients: selected.map((s) => ({
        compositionProductId: s.compositionProductId,
        quantity: s.quantity,
        quality: s.isSauce ? s.quality : null,
      })),
      disponibilities: disponibilities.filter((d) => d.startDate && d.endDate),
    };

    if (isEdit) {
      dispatch(productSuggestionsOperations.updateSuggestion(suggestion.id, payload));
    } else {
      dispatch(productSuggestionsOperations.createSuggestion(productId, payload));
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Modifier la suggestion" : "Nouvelle suggestion"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label>Titre de la suggestion</Form.Label>
            <Form.Control
              type="text"
              value={name}
              maxLength={128}
              placeholder="Ex. Salade fraîche concombre, maïs & basilic"
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Image</Form.Label>
                <div>
                  {mediaPreview && (
                    <Preview>
                      <img src={mediaPreview} alt="aperçu" />
                      <button
                        type="button"
                        onClick={() => {
                          setMediaId(null);
                          setMediaPreview(null);
                        }}
                      >
                        ✕
                      </button>
                    </Preview>
                  )}
                  <Form.Control type="file" accept="image/jpeg,image/png" onChange={onFileChange} />
                  {uploading && <Hint>Envoi de l'image…</Hint>}
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Badge (optionnel)</Form.Label>
                <Form.Control
                  type="text"
                  value={badge}
                  placeholder="Ex. Nouveauté"
                  onChange={(e) => setBadge(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Badge thématique</Form.Label>
                <Form.Control
                  as="select"
                  value={thematicBadgeId}
                  onChange={(e) => setThematicBadgeId(e.target.value)}
                >
                  <option value="">Aucun</option>
                  {(pictos || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Son ordre d'apparition</Form.Label>
                <Form.Control
                  type="number"
                  value={sort}
                  placeholder="Ex. 1"
                  onChange={(e) => setSort(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group>
            <Form.Label>Ingrédients</Form.Label>
            <Select
              options={ingredientOptions}
              value={null}
              placeholder="Rechercher un ingrédient à ajouter…"
              onChange={addIngredient}
              noOptionsMessage={() => "Aucun ingrédient"}
            />
            <Selected>
              {selected.map((s) => (
                <SelRow key={s.compositionProductId}>
                  <SelName>{s.name}</SelName>
                  {s.isSauce && (
                    <QualitySelect
                      value={s.quality || "normal"}
                      onChange={(e) => changeQuality(s.compositionProductId, e.target.value)}
                    >
                      {QUALITY_OPTIONS.map((q) => (
                        <option key={q.value} value={q.value}>
                          {q.label}
                        </option>
                      ))}
                    </QualitySelect>
                  )}
                  <Stepper>
                    <button type="button" onClick={() => changeQty(s.compositionProductId, -1)}>
                      −
                    </button>
                    <span>{s.quantity}</span>
                    <button type="button" onClick={() => changeQty(s.compositionProductId, 1)}>
                      ＋
                    </button>
                  </Stepper>
                  <RemoveBtn type="button" onClick={() => removeIngredient(s.compositionProductId)}>
                    ✕
                  </RemoveBtn>
                </SelRow>
              ))}
              {selected.length === 0 && <Hint>Aucun ingrédient sélectionné.</Hint>}
            </Selected>
          </Form.Group>

          <Form.Group>
            <Form.Label>Disponibilité</Form.Label>
            {disponibilities.map((d, index) => (
              <Row key={index} style={{ marginBottom: 8 }}>
                <Col xs={5}>
                  <Form.Control
                    type="date"
                    value={d.startDate}
                    onChange={(e) => changeDispo(index, "startDate", e.target.value)}
                  />
                </Col>
                <Col xs={5}>
                  <Form.Control
                    type="date"
                    value={d.endDate}
                    onChange={(e) => changeDispo(index, "endDate", e.target.value)}
                  />
                </Col>
                <Col xs={2}>
                  <Button variant="outline-danger" size="sm" onClick={() => removeDispo(index)}>
                    ✕
                  </Button>
                </Col>
              </Row>
            ))}
            <Button variant="light" size="sm" onClick={addDispo}>
              ＋ Ajouter une période
            </Button>
            <Hint>Sans aucune période renseignée, la suggestion n'apparaît pas en front. Ajoutez au moins une période de disponibilité pour la rendre visible.</Hint>
          </Form.Group>

          <Form.Group>
            <Form.Label>Options</Form.Label>
            <Form.Check
              type="checkbox"
              label="€ engagé"
              checked={isEngaged}
              onChange={(e) => setIsEngaged(e.target.checked)}
            />
            <Form.Check
              type="checkbox"
              label="Mettre dans grande boîte (forcé)"
              checked={forcedBigBox}
              onChange={(e) => setForcedBigBox(e.target.checked)}
            />
            <Form.Check
              type="checkbox"
              label="Confection en série"
              checked={massProduction}
              onChange={(e) => setMassProduction(e.target.checked)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Activation</Form.Label>
            <Form.Check
              type="checkbox"
              label="Actif pour les charrettes (spot)"
              checked={activeOnSpot}
              onChange={(e) => setActiveOnSpot(e.target.checked)}
            />
            <Form.Check
              type="checkbox"
              label="Actif pour la boutique"
              checked={activeOnShop}
              onChange={(e) => setActiveOnShop(e.target.checked)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Annuler
        </Button>
        <Button
          style={{ backgroundColor: PRIMARY, borderColor: PRIMARY }}
          onClick={handleSubmit}
          disabled={uploading}
        >
          ✓ Enregistrer la suggestion
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const Selected = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const SelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fafaf8;
  border: 1px solid #e4e4e1;
  border-radius: 8px;
  padding: 8px 10px;
`;
const SelName = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
`;
const QualitySelect = styled.select`
  font-size: 12px;
  border: 1px solid #f1e0c4;
  background: #fff6e9;
  border-radius: 7px;
  padding: 4px 6px;
`;
const Stepper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e4e4e1;
  border-radius: 7px;
  overflow: hidden;
  background: #fff;
  button {
    border: none;
    background: #fff;
    width: 30px;
    height: 30px;
    font-size: 16px;
    cursor: pointer;
    color: #357a43;
  }
  span {
    min-width: 32px;
    text-align: center;
    font-weight: 700;
    font-size: 13px;
  }
`;
const RemoveBtn = styled.button`
  border: none;
  background: none;
  color: #b23b3b;
  cursor: pointer;
  font-size: 16px;
`;
const Hint = styled.p`
  font-size: 12px;
  color: #6b6b68;
  margin: 6px 0 0;
`;
const Preview = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 8px;
  img {
    width: 90px;
    height: 90px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e4e4e1;
  }
  button {
    position: absolute;
    top: -8px;
    right: -8px;
    border: none;
    background: #b23b3b;
    color: #fff;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    cursor: pointer;
  }
`;

export default SuggestionModal;
